import { Participant, RemoteTrackPublication, Track } from 'livekit-client';
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { excludedPresenters } from '../../../utils/constants';

// Resolve publications even while useTracks returns a subscription placeholder.
export const getOutputCameraPublication = (
  trackRef: TrackReferenceOrPlaceholder,
): RemoteTrackPublication | undefined => {
  const { participant, source } = trackRef;
  const excluded = [...excludedPresenters, 'rtmp_ingress'].some(prefix =>
    participant.identity.toLowerCase().startsWith(prefix),
  );
  if (participant.isLocal || source !== Track.Source.Camera || excluded) {
    return undefined;
  }
  const publication =
    trackRef.publication ?? participant.getTrackPublication(source);
  return publication instanceof RemoteTrackPublication &&
    publication.kind === Track.Kind.Video &&
    publication.source === Track.Source.Camera
    ? publication
    : undefined;
};

export interface ICameraTarget {
  getElement: () => HTMLVideoElement | null;
  participant: Participant;
  publication: RemoteTrackPublication;
}

// Include ancestor opacity and clipping: mounted carousel tiles can be off-screen.
export const isVideoVisible = (element: HTMLVideoElement | null): boolean => {
  const view = element?.ownerDocument.defaultView;
  if (!element?.isConnected || !view || element.ownerDocument.hidden) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  let left = Math.max(0, rect.left);
  let top = Math.max(0, rect.top);
  let right = Math.min(view.innerWidth, rect.right);
  let bottom = Math.min(view.innerHeight, rect.bottom);
  let ancestor: HTMLElement | null = element;
  while (ancestor) {
    const style = view.getComputedStyle(ancestor);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse' ||
      Number(style.opacity) === 0
    ) {
      return false;
    }
    const bounds = ancestor.getBoundingClientRect();
    if (/(hidden|clip|scroll|auto)/.test(style.overflowX)) {
      left = Math.max(left, bounds.left);
      right = Math.min(right, bounds.right);
    }
    if (/(hidden|clip|scroll|auto)/.test(style.overflowY)) {
      top = Math.max(top, bounds.top);
      bottom = Math.min(bottom, bounds.bottom);
    }
    ancestor = ancestor.parentElement;
  }
  return right > left && bottom > top;
};

export const videoProgress = (
  element: HTMLVideoElement,
): number | undefined => {
  if (element.readyState < 2 || element.videoWidth === 0) return undefined;
  if (typeof element.getVideoPlaybackQuality === 'function') {
    const quality = element.getVideoPlaybackQuality();
    return quality.totalVideoFrames - quality.droppedVideoFrames;
  }
  // Older output browsers may not expose frame counters.
  return element.currentTime;
};
